/**
 * AIJumpTargetSection — "Jump to Workflow Section" orchestration UI.
 *
 * Used by TransferToAIEditor, NetsuiteAIEditor, and any other AI node editor.
 * Lets the user configure which named workflow sections (Section Entry nodes)
 * this AI can jump to based on detected intent.
 *
 * How it works at runtime:
 *   The AI's prompt gets a jump_to_{snake_case_name}(reason: string) tool for each
 *   jump target. When the AI detects the described intent, it calls the tool and
 *   workflow execution resumes from the target Section Entry node.
 */
import React, { useContext, useMemo } from "react";
import { WorkflowContext } from "@/context/WorkflowContext";
import { WorkflowNode, WorkflowJumpTarget } from "../../types";

interface Props {
  node: WorkflowNode;
  onUpdate: (node: Partial<WorkflowNode>) => void;
}

export default function AIJumpTargetSection({ node, onUpdate }: Props) {
  const { workflow } = useContext(WorkflowContext);

  // Find all workflowSection nodes in the current workflow (excluding self)
  const sectionNodes = useMemo(() => {
    if (!workflow) return [];
    return Object.values(workflow.nodes).filter(
      (n) => n.type === "action" && n.actionType === "workflowSection" && n.id !== node.id
    );
  }, [workflow, node.id]);

  const jumpTargets: WorkflowJumpTarget[] = Array.isArray(node.jumpTargets)
    ? node.jumpTargets
    : [];

  function toggleSection(sectionNode: WorkflowNode, checked: boolean) {
    const existing = jumpTargets.find((t) => t.nodeId === sectionNode.id);
    if (checked && !existing) {
      const next: WorkflowJumpTarget[] = [
        ...jumpTargets,
        {
          nodeId: sectionNode.id,
          name: sectionNode.sectionName || sectionNode.name || `Section ${sectionNode.id}`,
          when: ""
        }
      ];
      onUpdate({ jumpTargets: next });
    } else if (!checked && existing) {
      const next = jumpTargets.filter((t) => t.nodeId !== sectionNode.id);
      onUpdate({ jumpTargets: next.length > 0 ? next : undefined });
    }
  }

  function updateWhen(nodeId: number, when: string) {
    const next = jumpTargets.map((t) => (t.nodeId === nodeId ? { ...t, when } : t));
    onUpdate({ jumpTargets: next });
  }

  if (sectionNodes.length === 0) {
    return (
      <div className="mt-4">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
            Jump to Workflow Section
          </span>
          <span className="text-xs text-slate-400 italic">optional</span>
        </div>
        <p className="text-xs text-slate-500 leading-relaxed">
          No Section Entry nodes in this workflow yet. Add a{" "}
          <span className="font-medium text-slate-700">Section Entry</span> node to a part of
          the workflow you want this AI to be able to jump to based on intent.
        </p>
      </div>
    );
  }

  return (
    <div className="mt-4">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
          Jump to Workflow Section
        </span>
        <span className="text-xs text-slate-400 italic">optional</span>
      </div>
      <p className="text-xs text-slate-500 mb-3 leading-relaxed">
        When the AI detects the described intent, it jumps to that section of the workflow.
        Add a <span className="font-medium text-slate-700">Transfer to AI</span> node at the
        end of each section to return control here.
      </p>

      <div className="flex flex-col gap-2">
        {sectionNodes.map((sectionNode) => {
          const target = jumpTargets.find((t) => t.nodeId === sectionNode.id);
          const isChecked = !!target;
          const displayName =
            sectionNode.sectionName || sectionNode.name || `Section ${sectionNode.id}`;

          return (
            <div
              key={sectionNode.id}
              className={`rounded border p-2.5 transition-colors ${
                isChecked
                  ? "border-amber-300 bg-amber-50"
                  : "border-slate-200 bg-white hover:border-slate-300"
              }`}
            >
              <label className="flex items-start gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  className="w-4 h-4 mt-0.5 shrink-0"
                  checked={isChecked}
                  onChange={(e) => toggleSection(sectionNode, e.target.checked)}
                  aria-label={`Jump to section: ${displayName}`}
                />
                <div className="flex flex-col gap-1 min-w-0 flex-1">
                  <span className="text-sm font-medium text-slate-800">{displayName}</span>
                  {sectionNode.sectionDescription && (
                    <span className="text-xs text-slate-500">{sectionNode.sectionDescription}</span>
                  )}
                </div>
              </label>

              {isChecked && (
                <div className="mt-2 ml-6.5 pl-0.5">
                  <label className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-slate-700">
                      Jump here when the customer…
                    </span>
                    <input
                      type="text"
                      className="input text-xs"
                      placeholder='e.g. "wants to return an item, ask about a refund"'
                      value={target?.when ?? ""}
                      onChange={(e) => updateWhen(sectionNode.id, e.target.value)}
                    />
                    <span className="text-xs text-slate-400">
                      The AI uses this to decide when to jump here.
                    </span>
                  </label>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
